import React from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import naraTheme from './theme';
import LandingPage from './components/LandingPage';
import Login from './components/Login';
import Register from './components/Register.jsx';
import PrivateRoute from './PrivateRoute';
import Layout from './components/Layout';
import WhatsappChatbotInfo from './components/WhatsappChatbotInfo';
import WebsiteChatbotInfo from './components/WebsiteChatbotInfo';
import ImageUpload from './components/ImageUpload';
import Analytics from './components/Analytics';
import AnalyticsLayout from './components/analytics/AnalyticsLayout';
import EMRDashboard from './components/emr/EMRDashboard';
import AgendaCitasPage from './components/emr/AgendaCitasPage';
import ConsumoTokens from './components/ConsumoTokens';
import Suscripcion from './components/Suscripcion';
import { SmartFlowsList, SmartFlowEditor } from './components/SmartFlowBuilder';
import AdminPlanes from './components/AdminPlanes';
import AdminServices from './components/AdminServices';
import AdminPoliticas from './components/AdminPoliticas';
import AdminTests from './components/AdminTests';
import AdminCorrections from './components/AdminCorrections';
import AdminDB from './components/AdminDB';
import AdminTiposEmpresa from './components/AdminTiposEmpresa';
import AdminTimezone from './components/AdminTimezone';
import AdminTrazabilidad from './components/AdminTrazabilidad';
import AdminPasarelas from './components/AdminPasarelas';
import MarcoLegalDatos from './components/MarcoLegalDatos';
import PasadiasLayout from './components/pasadias/PasadiasLayout';
import PasadiasCalendario from './components/pasadias/CalendarioPage';
import PasadiasSalidas from './components/pasadias/SalidasPage';
import PasadiasGenerar from './components/pasadias/GenerarMasivasPage';
import PasadiasAsignar from './components/pasadias/AsignarBotesPage';
import PasadiasClima from './components/pasadias/ClimaPage';
import PasadiasHistorico from './components/pasadias/HistoricoPage';
import ConversacionesDashboard from './components/ConversacionesDashboard';
import AdminTurismo from './components/AdminTurismo';
import AdminAgencias from './components/AdminAgencias';
import AdminCuentasDestino from './components/AdminCuentasDestino';
import AdminCxC from './components/AdminCxC';
import AdminComisiones, {
  CarteraTuristasTab, VouchersTab, VentasDirectasTab,
  DeudaTab, DashboardTab, HistorialTab, CruceTab,
} from './components/AdminComisiones';
import AgenciasLayout from './components/agencias/AgenciasLayout';
import FinanzasLayout from './components/finanzas/FinanzasLayout';
import EstadoCuentaAgencia from './components/finanzas/EstadoCuentaAgencia';
// PagosAgencias se retiró el 11/09/2026: registraba pagos de agencia sin
// comprobante y sin pasar por el control de comprobante repetido. Lo que
// hacía lo hace el modal de CxC, que además exige evidencia. El archivo se
// conserva en disco por si hay que consultar algo.
import AgingCartera from './components/finanzas/AgingCartera';
import FotoDiaPage from './components/finanzas/FotoDiaPage';
import AdminNoShows from './components/finanzas/AdminNoShows';
import PagosPorReserva from './components/finanzas/PagosPorReserva';
import PendientesSubsanar from './components/finanzas/PendientesSubsanar';
import NotasContables from './components/finanzas/NotasContables';
import CorreccionesPage from './components/booking/CorreccionesPage';
import CronogramaComisiones from './components/finanzas/CronogramaComisiones';
import GerencialLayout from './components/gerencial/GerencialLayout';
import SolicitudesActualizacionPage from './components/agencias/SolicitudesActualizacionPage';
import AdminSalidas from './components/AdminSalidas';
import MuellePage from './components/muelle/MuellePage';
import CierreMuelle from './components/muelle/CierreMuelle';
import MuelleLayout from './components/muelle/MuelleLayout';
import ReportesLayout from './components/reportes/ReportesLayout';
import ReportePasajerosPage from './components/reportes/ReportePasajerosPage';
import ReporteAnexoVentasPage from './components/reportes/ReporteAnexoVentasPage';
import ReporteEmbarquesPage from './components/reportes/ReporteEmbarquesPage';
import ReporteChartersPage from './components/reportes/ChartersPage';
import FlotaLayout from './components/flota/FlotaLayout';
import FlotaEmbarcaciones from './components/flota/EmbarcacionesPage';
import FlotaProximos from './components/flota/ProximosPage';
import FlotaEnCurso from './components/flota/EnCursoPage';
import FlotaHistorico from './components/flota/HistoricoPage';
import FlotaPlantillas from './components/flota/PlantillasPage';
import FlotaCombustible from './components/flota/CombustibleLayout';
import BookingLayout from './components/booking/BookingLayout';
import BookingHoy from './components/booking/HoyPage';
import BookingProximas from './components/booking/ProximasPage';
import BookingCalendario from './components/booking/CalendarioPage';
import BookingOperacion from './components/booking/OperacionDiaPage';
import BookingPorCobrar from './components/booking/PorCobrarPage';
import BookingHistorico from './components/booking/HistoricoPage';
import BookingHold from './components/booking/HoldPage';
import CharterLayout from './components/charter/CharterLayout';
import CharterListado from './components/charter/ListadoPage';
import CharterCalendario from './components/charter/CalendarioPage';
import CharterNuevo from './components/charter/NuevoPage';
import CharterDetalle from './components/charter/DetallePage';
import AdminUsuarios from './components/AdminUsuarios';
import MiEquipo from './components/MiEquipo';
import MiActividad from './components/MiActividad';
import PendingApproval from './components/PendingApproval';
import NaraChat from './components/NaraChat';
import HubAuth from './components/HubAuth';
import ReservaForm from './pages/ReservaForm';
import AgenciaActualizar from './pages/AgenciaActualizar';
import VoucherPublico from './pages/VoucherPublico';
import ComercialLayout from './components/comercial/ComercialLayout';
import ComercialBitacora from './components/comercial/BitacoraPage';
import ComercialProspectos from './components/comercial/ProspectosPage';
import ComercialCartera from './components/comercial/MiCarteraPage';
import ComercialDashboard from './components/comercial/DashboardPage';
import ComercialPendientes from './components/comercial/PendientesPage';
import ComercialAlertas from './components/comercial/AlertasPage';
import ComercialLag from './components/comercial/EficienciaPage';
import ComercialVentas from './components/comercial/VentasDiaPage';
import AdminIntermediarios from './components/AdminIntermediarios';
import CatalogoLayout from './components/catalogo/CatalogoLayout';
import DestinosPage from './components/catalogo/DestinosPage';
import PaquetesPage from './components/catalogo/PaquetesPage';
import CatalogoPublico from './components/CatalogoPublico';
import AlquileresPage from './components/catalogo/AlquileresPage';
import PuntosEmbarquePage from './components/catalogo/PuntosEmbarquePage';
import Configuracion from './components/Configuracion';
import './App.css';

// Oculta el warning de ResizeObserver en todos los entornos
window.addEventListener('error', function(e) {
  if (e.message && e.message.includes('ResizeObserver loop completed')) {
    e.preventDefault();
  }
});


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000,       // 30s — usa cache mientras refresca
      gcTime: 5 * 60 * 1000,      // 5 min — mantiene en memoria
      refetchOnWindowFocus: true,
      retry: 2,
      retryDelay: a => Math.min(1000 * 2 ** a, 8000),
    },
    mutations: { retry: 1 },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
    <ThemeProvider theme={naraTheme}>
    <CssBaseline />
    <Router>
      <Routes>
        <Route path="/hub-auth" element={<HubAuth />} />
        <Route path="/r/:token" element={<ReservaForm />} />
        <Route path="/v/:codigo" element={<VoucherPublico />} />
        <Route path="/agencia-actualizar/:token" element={<AgenciaActualizar />} />
        {/* Mejora #9 — Catálogo público shareable (sin auth) */}
        <Route path="/c/:empresaId" element={<CatalogoPublico />} />
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        {/* Rutas obsoletas heredadas de NARA Care — redirigir a Booking */}
        <Route path="/dashboard" element={<Navigate to="/booking" replace />} />
        <Route path="/constructor" element={<Navigate to="/booking" replace />} />
        <Route path="/analytics" element={<PrivateRoute><Layout><AnalyticsLayout /></Layout></PrivateRoute>} />
        <Route path="/emr/*" element={<PrivateRoute><Layout><EMRDashboard /></Layout></PrivateRoute>} />
        <Route path="/agenda" element={<PrivateRoute><Layout><AgendaCitasPage /></Layout></PrivateRoute>} />
        <Route path="/whatsapp" element={<Navigate to="/analytics?tab=whatsapp" replace />} />
        <Route path="/consumo-tokens" element={<PrivateRoute><Layout><ConsumoTokens /></Layout></PrivateRoute>} />
        <Route path="/suscripcion" element={<PrivateRoute><Layout><Suscripcion /></Layout></PrivateRoute>} />
        <Route path="/smart-flows" element={<PrivateRoute><Layout><SmartFlowsList /></Layout></PrivateRoute>} />
        <Route path="/smart-flows/:chatbotId" element={<PrivateRoute><Layout><SmartFlowEditor /></Layout></PrivateRoute>} />
        <Route path="/admin/planes" element={<PrivateRoute><Layout><AdminPlanes /></Layout></PrivateRoute>} />
        <Route path="/admin/servicios" element={<PrivateRoute><Layout><AdminServices /></Layout></PrivateRoute>} />
        <Route path="/admin/politicas" element={<PrivateRoute><Layout><AdminPoliticas /></Layout></PrivateRoute>} />
        <Route path="/configuracion/pasarelas" element={<PrivateRoute><Layout><AdminPasarelas /></Layout></PrivateRoute>} />
        <Route path="/marco-legal" element={<PrivateRoute><Layout><MarcoLegalDatos /></Layout></PrivateRoute>} />
        <Route path="/admin/tests" element={<PrivateRoute><Layout><AdminTests /></Layout></PrivateRoute>} />
        <Route path="/admin/corrections" element={<PrivateRoute><Layout><AdminCorrections /></Layout></PrivateRoute>} />
        <Route path="/admin/db" element={<PrivateRoute><Layout><AdminDB /></Layout></PrivateRoute>} />
        <Route path="/admin/tipos-empresa" element={<PrivateRoute><Layout><AdminTiposEmpresa /></Layout></PrivateRoute>} />
        <Route path="/admin/timezone" element={<PrivateRoute><Layout><AdminTimezone /></Layout></PrivateRoute>} />
        <Route path="/trazabilidad" element={<PrivateRoute><Layout><AdminTrazabilidad /></Layout></PrivateRoute>} />
        <Route path="/mi-equipo" element={<PrivateRoute><Layout><MiEquipo /></Layout></PrivateRoute>} />
        <Route path="/mi-actividad" element={<PrivateRoute><Layout><MiActividad /></Layout></PrivateRoute>} />
        <Route path="/intermediarios" element={<PrivateRoute><Layout><AdminIntermediarios /></Layout></PrivateRoute>} />
        <Route path="/admin/usuarios" element={<PrivateRoute><Layout><AdminUsuarios /></Layout></PrivateRoute>} />
        <Route path="/admin/cuentas-destino" element={<PrivateRoute><Layout><AdminCuentasDestino /></Layout></PrivateRoute>} />
        <Route path="/pending-approval" element={<PendingApproval />} />
        <Route path="/conversaciones" element={<PrivateRoute><Layout><ConversacionesDashboard /></Layout></PrivateRoute>} />

        {/* Catálogo modernizado — cada entidad es su propia URL */}
        <Route path="/catalogo" element={<PrivateRoute><Layout><CatalogoLayout /></Layout></PrivateRoute>}>
          <Route path="destinos"        element={<DestinosPage />} />
          <Route path="paquetes"        element={<PaquetesPage />} />
          <Route path="alquileres"      element={<AlquileresPage />} />
          <Route path="puntos-embarque" element={<PuntosEmbarquePage />} />
        </Route>
        {/* Redirect ruta vieja: /catalogo/embarcaciones → /flota/embarcaciones */}
        <Route path="/catalogo/embarcaciones" element={<Navigate to="/flota/embarcaciones" replace />} />

        {/* Configuración sacada de Catálogos */}
        <Route path="/configuracion" element={<PrivateRoute><Layout><Configuracion /></Layout></PrivateRoute>} />

        {/* Redirect URL vieja */}
        <Route path="/paquetes" element={<Navigate to="/catalogo/paquetes" replace />} />
        {/* Agencias — CRM de revendedores. La parte financiera vive ahora en /finanzas. */}
        <Route path="/agencias" element={<PrivateRoute><Layout><AgenciasLayout /></Layout></PrivateRoute>}>
          <Route path="lista"       element={<AdminAgencias />} />
          <Route path="solicitudes" element={<SolicitudesActualizacionPage />} />
        </Route>
        {/* Finanzas — sidebar agrupada en 3 secciones: Cobros, Agencias, Conciliación. */}
        <Route path="/finanzas" element={<PrivateRoute><Layout><FinanzasLayout /></Layout></PrivateRoute>}>
          <Route path="foto-dia"        element={<FotoDiaPage />} />
          <Route path="no-shows"        element={<AdminNoShows />} />
          <Route path="pagos-por-reserva" element={<PagosPorReserva />} />
          <Route path="pendientes-subsanar" element={<PendientesSubsanar />} />
          <Route path="cartera"         element={<Box sx={{ p: { xs: 2, sm: 3 } }}><CarteraTuristasTab /></Box>} />
          <Route path="vouchers"        element={<Box sx={{ p: { xs: 2, sm: 3 } }}><VouchersTab /></Box>} />
          <Route path="ventas-directas" element={<Box sx={{ p: { xs: 2, sm: 3 } }}><VentasDirectasTab /></Box>} />
          <Route path="estado-cuenta"   element={<Box sx={{ p: { xs: 2, sm: 3 } }}><EstadoCuentaAgencia /></Box>} />
          <Route path="deuda"           element={<Box sx={{ p: { xs: 2, sm: 3 } }}><DeudaTab /></Box>} />
          <Route path="cxc"             element={<AdminCxC />} />
          <Route path="aging"           element={<Box sx={{ p: { xs: 2, sm: 3 } }}><AgingCartera /></Box>} />
          <Route path="notas"           element={<Box sx={{ p: { xs: 2, sm: 3 } }}><NotasContables /></Box>} />
          <Route path="comisiones"      element={<Box sx={{ p: { xs: 2, sm: 3 } }}><DashboardTab /></Box>} />
          <Route path="cronograma"      element={<CronogramaComisiones />} />
          <Route path="liquidaciones"   element={<Box sx={{ p: { xs: 2, sm: 3 } }}><HistorialTab /></Box>} />
          <Route path="cruce"           element={<Box sx={{ p: { xs: 2, sm: 3 } }}><CruceTab /></Box>} />
        </Route>
        {/* Redirects de rutas viejas para que bookmarks/links externos sigan funcionando */}
        <Route path="/comisiones" element={<Navigate to="/finanzas/comisiones" replace />} />
        <Route path="/agencias/comisiones" element={<Navigate to="/finanzas/comisiones" replace />} />
        {/* Gerencial — módulo top-level para dirección. Reusa componentes ya
            existentes. Solo admin/superadmin acceden (gate en Layout.js). */}
        <Route path="/gerencial" element={<PrivateRoute><Layout><GerencialLayout /></Layout></PrivateRoute>}>
          <Route path="proyeccion-pagos" element={<CronogramaComisiones />} />
        </Route>
        {/* Comercial / CRM — bitácora de visitas, prospectos, cartera 360, dashboard */}
        <Route path="/comercial" element={<PrivateRoute><Layout><ComercialLayout /></Layout></PrivateRoute>}>
          <Route path="pendientes" element={<ComercialPendientes />} />
          <Route path="alertas"    element={<ComercialAlertas />} />
          <Route path="visitas"    element={<ComercialBitacora />} />
          <Route path="prospectos" element={<ComercialProspectos />} />
          <Route path="cartera"    element={<ComercialCartera />} />
          <Route path="dashboard"  element={<ComercialDashboard />} />
          <Route path="lag"        element={<ComercialLag />} />
          <Route path="ventas"     element={<ComercialVentas />} />
        </Route>
        {/* Booking — patrón sidebar secundaria + sub-rutas (igual que Catálogo/Pasadías) */}
        <Route path="/booking" element={<PrivateRoute><Layout><BookingLayout /></Layout></PrivateRoute>}>
          <Route path="hoy"        element={<BookingHoy />} />
          <Route path="proximas"   element={<BookingProximas />} />
          <Route path="calendario" element={<BookingCalendario />} />
          <Route path="operacion"  element={<BookingOperacion />} />
          <Route path="por-cobrar" element={<BookingPorCobrar />} />
          <Route path="hold"       element={<BookingHold />} />
          <Route path="historico"  element={<BookingHistorico />} />
          <Route path="correcciones" element={<CorreccionesPage />} />
        </Route>
        {/* Pasadías — nuevo módulo con secondary sidebar (mismo patrón que Catálogo) */}
        <Route path="/pasadias" element={<PrivateRoute><Layout><PasadiasLayout /></Layout></PrivateRoute>}>
          <Route path="calendario" element={<PasadiasCalendario />} />
          <Route path="salidas"    element={<PasadiasSalidas />} />
          <Route path="generar"    element={<PasadiasGenerar />} />
          <Route path="asignar"    element={<PasadiasAsignar />} />
          <Route path="clima"      element={<PasadiasClima />} />
          <Route path="historico"  element={<PasadiasHistorico />} />
        </Route>
        {/* Redirect URL vieja /salidas → /pasadias/salidas */}
        <Route path="/salidas" element={<Navigate to="/pasadias/salidas" replace />} />
        {/* Muelle — sidebar secundaria + tabs móviles (Recepción / Cierre) */}
        <Route path="/muelle" element={<PrivateRoute><Layout><MuelleLayout /></Layout></PrivateRoute>}>
          <Route path="recepcion" element={<MuellePage />} />
          <Route path="cierre"    element={<CierreMuelle />} />
        </Route>

        {/* Charter — alquiler de embarcaciones (rol operaciones/admin/superadmin) */}
        <Route path="/charter" element={<PrivateRoute><Layout><CharterLayout /></Layout></PrivateRoute>}>
          <Route path="listado"    element={<CharterListado />} />
          <Route path="calendario" element={<CharterCalendario />} />
          <Route path="nuevo"      element={<CharterNuevo />} />
          <Route path=":id"        element={<CharterDetalle />} />
        </Route>

        {/* Reportes — analítica multi-período de operación */}
        <Route path="/reportes" element={<PrivateRoute><Layout><ReportesLayout /></Layout></PrivateRoute>}>
          <Route path="embarques"          element={<ReporteEmbarquesPage />} />
          <Route path="pasajeros-por-tour" element={<ReportePasajerosPage />} />
          <Route path="anexo-ventas"       element={<ReporteAnexoVentasPage />} />
          <Route path="charters"           element={<ReporteChartersPage />} />
        </Route>

        {/* Flota — mantenimiento de embarcaciones */}
        <Route path="/flota" element={<PrivateRoute><Layout><FlotaLayout /></Layout></PrivateRoute>}>
          <Route path="proximos"      element={<FlotaProximos />} />
          <Route path="en-curso"      element={<FlotaEnCurso />} />
          <Route path="historico"     element={<FlotaHistorico />} />
          <Route path="plantillas"    element={<FlotaPlantillas />} />
          <Route path="embarcaciones" element={<FlotaEmbarcaciones />} />
          <Route path="combustible/*" element={<FlotaCombustible />} />
        </Route>
        <Route path="/whatsapp-chatbot" element={<WhatsappChatbotInfo />} />
        <Route path="/website-chatbot" element={<WebsiteChatbotInfo />} />
        <Route path="/subir-imagen/:token" element={<ImageUpload />} />
        <Route path="*" element={<Login />} />
      </Routes>
      <NaraChat />
    </Router>
    </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
